import Box from 'components/Box';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import userSelectors from 'features/user/services/selectors';
import { formatPhone, maskPhone } from 'utils/formatPhone';
import dayjs from 'dayjs';
import { formatCurrency } from 'utils/formatCurrency';
import PinCodeModal, { PinCodeModalRef } from 'features/appointment/pages/AppointmentsPage/components/Calendar/TableView/Body/PinCodeModal';

const LastTicketPopoverContent = () => {
    const pinCodeModalRef = useRef<PinCodeModalRef>(null);
    const lastTicket = userSelectors.lastTicket();
    const endTime = lastTicket?.endTime ? dayjs(lastTicket?.endTime).format('hh:mm A') : '';
    const [mask, setmask] = useState(true);

    const handleClickOpen = () => {
        pinCodeModalRef.current?.open(() => {
            setmask(false);
        });
    };

    const handleClickClose = () => {
        setmask(true);
    };

    const itemCreditCard = lastTicket?.isMultiplePayCard ? lastTicket?.paymentMultipleCard?.[0] : lastTicket?.payments?.[0];

    const RowDisplay = ({ label, content, className }: { label: string, content: React.ReactNode, className?: string }) => (
        <Box display='flex' alignItems='center' gap='4' className={className}>
            <LeftLabel>{label}</LeftLabel>
            {content}
        </Box>
    );

    const CustomerContent = () => (
        <Box display='flex' flexDirection='column'>
            <CustomerInfoText>
                {lastTicket?.customerName || 'Unknown Customer'}
            </CustomerInfoText>

            {lastTicket?.customerPhone && (
                <Box display='flex' alignItems='center' gap='1'>
                    <CustomerInfoText className='phone'>
                        {mask ? maskPhone(lastTicket?.customerPhone) : formatPhone(lastTicket?.customerPhone)}
                    </CustomerInfoText>
                    {mask ? (
                        <div onClick={handleClickOpen} style={{ cursor: 'pointer' }}>
                            <LockIcon />
                        </div>
                    ) : (
                        <div onClick={handleClickClose} style={{ cursor: 'pointer' }}>
                            <UnlockIcon />
                        </div>
                    )}
                </Box>
            )}

        </Box>
    );

    return (
        <Container>
            <HeaderContainer>
                Last Service Info
            </HeaderContainer>
            {lastTicket ? (
                <BodyContainer>
                    <RowDisplay className='item' label='Customer:' content={<CustomerContent />} />
                    {endTime ? (
                        <RowDisplay className='item' label='Time:' content={<TextContent className='ellipsis blue'>{endTime}</TextContent>} />
                    ) : null}
                    {lastTicket?.ticketNumber ? (
                        <RowDisplay className='item' label='Ticket:' content={<TicketNumberContainer className='ellipsis'>#{lastTicket?.ticketNumber}</TicketNumberContainer>} />
                    ) : null}
                    {itemCreditCard?.transNumber ? (
                        <RowDisplay className='item' label='Transaction:' content={<TextContent className='ellipsis'>#{itemCreditCard?.transNumber}</TextContent>} />
                    ) : null}
                    {itemCreditCard?.last4 ? (
                        <RowDisplay className='item' label='Payment:' content={<TextContent className='ellipsis'>Card {itemCreditCard?.last4}</TextContent>} />
                    ) : null}
                    {lastTicket?.tip ? (
                        <RowDisplay className='item' label='Tip:' content={<TextContent className='green'>{formatCurrency(lastTicket?.tip)}</TextContent>} />
                    ) : null}
                    {itemCreditCard?.authCode ? (
                        <RowDisplay className='item' label='Authorize:' content={<TextContent>{itemCreditCard?.authCode}</TextContent>} />
                    ) : null}
                    {itemCreditCard?.amount ? (
                        <RowDisplay className='item' label='Amount:' content={<TextContent>{formatCurrency(itemCreditCard?.amount)}</TextContent>} />
                    ) : null}
                </BodyContainer>
            ) : null}
            <PinCodeModal ref={pinCodeModalRef} title='ADMIN PASSWORD' />
        </Container>
    );
};

export default LastTicketPopoverContent;

const Container = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 8px;
    background: #F6F7FC;
    box-shadow: 0 4px 8px 0 rgba(0, 0, 0, 0.15);
    min-width: 300px;
`;

const HeaderContainer = styled.div`
    display: flex;
    padding: 16px 8px;
    align-items: center;
    align-self: stretch;
    color: #346751;
    font-family: Poppins;
    font-size: 24px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: uppercase;
`;

const BodyContainer = styled.div`
    display: flex;
    padding: 0 24px 24px 24px;
    flex-direction: column;
    align-self: stretch;
    gap: 4px;
    .item {
        padding: 6px 0;
    }
    .item:not(:last-child) {
        border-bottom: 1px solid #E9EDF2;
    }
`;

const TicketNumberContainer = styled.div`
    display: flex;
    padding: 6px 8px 4px;
    justify-content: center;
    align-items: center;
    border-radius: 5px;
    background: #232F3E;
    color: #FFF;
    font-family: Poppins;
    text-align: right;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    text-transform: uppercase;
`;

const CustomerInfoText = styled.div`
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    overflow: hidden;
    color: #1D2129;
    text-overflow: ellipsis;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-transform: capitalize;
    .phone {
        font-size: 16px;
    }
`;
const LeftLabel = styled.div`
    width: 150px;
    text-align: left;
    color: #505050;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    text-transform: uppercase;
`;

const TextContent = styled.span`
    color: #1D2129;
    font-family: Poppins;
    font-size: 18px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    &.blue {
        color: #4384FF;
    }
    &.green {
        color: #0DA472;
        font-size: 20px;
        font-weight: 600;
    }
    &.ellipsis {
        overflow: hidden;
        text-overflow: ellipsis;
        display: -webkit-box;
        -webkit-box-orient: vertical;
        -webkit-line-clamp: 1;
    }
    &.bold {
        font-weight: 600;
    }
`;

const LockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <g clipPath="url(#clip0_22296_127338)">
            <path d="M8.98568 0.645648C10.6478 -0.112094 12.5963 -0.203901 14.3269 0.37571C16.8221 1.18415 18.7514 3.3779 19.3803 5.90736C19.7626 7.3009 19.5845 8.75609 19.6283 10.1811C20.1956 10.4538 20.5985 11.0348 20.5875 11.672C20.5889 15.2346 20.5875 18.7972 20.5889 22.3598C20.6245 23.1957 19.9106 24.0055 19.0583 23.9904C14.3474 24.0014 9.63517 23.9767 4.92292 24C4.0816 23.9849 3.37044 23.1984 3.41155 22.3612C3.41292 18.7958 3.41018 15.2305 3.41429 11.6651C3.38551 11.0156 3.82947 10.484 4.3529 10.1647C4.39949 8.79172 4.25014 7.39681 4.58721 6.04987C5.11202 3.69854 6.77548 1.61989 8.98568 0.645648ZM10.334 3.22718C9.06652 3.72457 8.06625 4.80569 7.60174 6.08002C7.10708 7.3516 7.34139 8.73828 7.28521 10.066C10.4327 10.066 13.5801 10.0674 16.7262 10.0647C16.6837 8.83831 16.8687 7.57358 16.4946 6.38147C16.1013 5.08933 15.186 3.93833 13.9542 3.35735C12.8388 2.80514 11.4864 2.76267 10.334 3.22718ZM10.5628 14.5344C10.3299 15.2428 10.7355 16.0047 11.3726 16.3376C11.2151 17.1926 11.0493 18.0477 10.8807 18.9013C11.6152 19.0123 12.3619 18.9274 13.1019 18.9534C12.932 18.097 12.7621 17.2392 12.5387 16.3952C12.8922 16.1677 13.2567 15.895 13.3924 15.4757C13.6719 14.7632 13.3006 13.8767 12.5853 13.5944C11.8029 13.2286 10.793 13.7081 10.5628 14.5344Z" fill="#232F3E" />
        </g>
        <defs>
            <clipPath id="clip0_22296_127338">
                <rect width="24" height="24" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const UnlockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
        <g clipPath="url(#clip0_18470_196786)">
            <path d="M8.64261 0.840943C10.5002 -0.172185 12.8186 -0.275756 14.7612 0.5621C17.6014 1.73457 19.4803 4.77926 19.3528 7.83326C19.2532 8.83311 17.9652 9.43461 17.1606 8.80788C16.4967 8.40688 16.5671 7.573 16.494 6.90776C16.2364 4.57345 14.0136 2.57508 11.6289 2.8194C9.4247 2.98405 7.64143 4.95586 7.45686 7.11756C7.3918 8.25285 7.44491 9.39345 7.42765 10.5314C11.2199 10.5354 15.0122 10.5261 18.8044 10.5354C19.6144 10.5314 20.3128 11.2816 20.285 12.0849C20.2876 15.5386 20.2876 18.9909 20.285 22.4446C20.3128 23.2944 19.5347 24.042 18.6876 23.9982C14.2128 23.9968 9.73673 23.9995 5.26197 23.9968C4.47723 24.0181 3.71373 23.3608 3.71771 22.5575C3.7124 19.0215 3.70975 15.4855 3.71904 11.9508C3.7124 11.3732 4.13332 10.9019 4.61665 10.6403C4.6764 8.88224 4.42013 7.07241 4.95923 5.36483C5.5355 3.4541 6.87262 1.77573 8.64261 0.840943ZM10.5826 14.9955C10.4498 15.6329 10.8109 16.2756 11.3686 16.5783C11.2637 17.4228 11.0592 18.2527 10.9185 19.0919C11.6382 19.0972 12.3579 19.0972 13.0775 19.0919C12.8903 18.2673 12.7084 17.4414 12.5345 16.6141C12.9155 16.4123 13.2634 16.0976 13.3763 15.6661C13.6233 14.9318 13.1373 14.0754 12.395 13.8788C11.6129 13.6239 10.7021 14.1816 10.5826 14.9955Z" fill="#232F3E" />
        </g>
        <defs>
            <clipPath id="clip0_18470_196786">
                <rect width="24" height="24" fill="white" />
            </clipPath>
        </defs>
    </svg>
);